import React from "react";

import { Check } from "@mui/icons-material";
import { Box, Typography } from "@mui/material";

import { BulletinSectionId } from "@/components/alerts/add-v2/sections/sectionIds";

export interface SectionRailItem {
  id: BulletinSectionId;
  label: string;
  complete: boolean;
}

interface SectionRailProps {
  items: SectionRailItem[];
  /** The section the user is working in — see `BulletinFormPage`; not a scroll position. */
  activeId: BulletinSectionId | null;
  onJump: (id: BulletinSectionId) => void;
  eyebrow: string;
  progressLabel: string;
  /** Finalize, the required-field count and the save indicator live here on lg. */
  footer?: React.ReactNode;
}

/**
 * The lg/md navigation column: jump links, the active-section highlight and completion ticks.
 *
 * Rebuilt from the production stepper's visual language (24px circles, a check once complete,
 * hairline connectors) but laid out vertically, since sections are a page rather than steps.
 */
export const SectionRail: React.FC<SectionRailProps> = ({
  items,
  activeId,
  onJump,
  eyebrow,
  progressLabel,
  footer,
}) => {
  return (
    <Box
      component="nav"
      sx={{
        // Stickiness is the shell's job — see the wrapper in `SplitFormShell`. This just fills the
        // height it is given, with the list taking the slack so the footer stays reachable.
        flex: 1,
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Typography
        variant="caption"
        sx={{
          fontWeight: 700,
          letterSpacing: ".8px",
          textTransform: "uppercase",
          color: "text.secondary",
          px: 1.25,
          pb: 0.5,
        }}
      >
        {eyebrow}
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ px: 1.25 }}>
        {progressLabel}
      </Typography>

      <Box sx={{ mt: 1.5, minHeight: 0, overflowY: "auto" }}>
        {items.map((item, index) => {
          const active = item.id === activeId;
          return (
            <Box
              key={item.id}
              component="button"
              type="button"
              onClick={() => onJump(item.id)}
              aria-current={active ? "true" : undefined}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.25,
                width: "100%",
                px: 1.25,
                py: 1,
                border: 0,
                borderRadius: 1,
                background: active ? "action.selected" : "transparent",
                backgroundColor: active ? "action.selected" : "transparent",
                cursor: "pointer",
                textAlign: "left",
                font: "inherit",
                "&:hover": { backgroundColor: "action.hover" },
              }}
            >
              <Box
                aria-hidden
                sx={{
                  flex: "0 0 auto",
                  width: 24,
                  height: 24,
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 12,
                  color: "common.white",
                  backgroundColor:
                    item.complete || active
                      ? "primary.main"
                      : "action.disabled",
                }}
              >
                {item.complete ? <Check sx={{ fontSize: 14 }} /> : index + 1}
              </Box>
              <Typography
                variant="body2"
                sx={{ fontWeight: active ? 600 : 400, minWidth: 0 }}
              >
                {item.label}
              </Typography>
            </Box>
          );
        })}
      </Box>

      {footer && (
        <Box
          sx={{
            // Normally the rail is content-height and this sits just under the list. On a short
            // viewport, where the rail hits its max height and the list scrolls, `mt: auto` keeps
            // the actions pinned at the bottom instead of scrolling out of reach.
            mt: "auto",
            pt: 2,
            px: 1.25,
            borderTop: 1,
            borderColor: "divider",
          }}
        >
          {footer}
        </Box>
      )}
    </Box>
  );
};
